import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import Modal from '@mui/material/Modal';

import { Box, Tabs, Tab } from '@mui/material';
import CheckSheet from 'src/screen/Protected/DeviceScreen/TestingReport/CheckSheet';
import CommissioningSignOff from 'src/screen/Protected/DeviceScreen/Commissioning/CommissioningSignOff';
import Button from 'src/component/UI/Button';
import { getDeviceById } from 'src/module/device/selector';
import { Map, List, fromJS } from 'immutable';
import { getAbilities } from 'src/module/authentication/selector';
import { fetchCheckSheet, saveCheckSheet, updateCheckSheet, fetchCheckSheetTasks } from 'src/module/checkSheet/action';
import { getCheckSheetReport, getCheckSheetTasks, getCheckSheetTasksLoading, getSaveCheckSheetReportLoading } from 'src/module/checkSheet/selector';
import LogoSpinner from 'src/component/UI/LogoSpinner';
import useMediaQuery from '@mui/material/useMediaQuery';
import variables from 'src/style/variable/variables.module.scss';
import { useTranslation } from 'src/i18n';
import CommLoopInfo, { CELLULAR_RADIO_SECTION_KEYS } from './CommLoopInfo';
import CircuitDynamicTable from '../CommissioningCircuit/CircuitDynamicTable';
import './CommissioningCommLoopReport.scss';

const selectGeneralTasks = getCheckSheetTasks('commissioning-comm-loop-v2-general');
const SIGN_OFF_TAB = '6';

export default function CommissioningCommLoopReport (props) {
  const {
    deviceId,
    reportId,
    open
  } = props;

  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [tabValue, setTabValue] = useState('1');
  const generalTasks = useSelector(selectGeneralTasks);
  const report = useSelector(getCheckSheetReport(reportId));
  const device = useSelector(getDeviceById(deviceId));
  const isSaveLoading = useSelector(getSaveCheckSheetReportLoading('commissioning-comm-loop-v2'));
  const isLoadingTasks = useSelector(getCheckSheetTasksLoading);
  const isMobile = useMediaQuery(`(max-width: ${variables.mobileWidth})`);
  const abilities: any = useSelector(getAbilities);
  const isPhoneView = useMediaQuery(`(max-width: ${variables.phoneWidth})`);

  const canEdit = abilities.includes('edit-check-sheet-report');
  const readOnly = Boolean(reportId) && !canEdit;

  const buildEmptyResults = (tasks) => {
    const tasksList = tasks.toJS ? tasks.toJS() : [];
    return fromJS({
      info: {
        type: 'commissioning-comm-loop-v2',
        deviceId: deviceId || null,
      },
      'commissioning-comm-loop-v2-general': tasksList.map((task: any) => ({
        task_id: task.key,
        status: null,
        comment: '',
      })),
      extras: {
        projectInfo: device ? { commLoopNumber: device.get('tag') } : {},
        networkSwitch: {},
        protocolConverter: {},
        cellularRouter: {},
        masterRadio: {},
        slaveRadio: {},
        deviceList: [],
        deficiencies: [],
      },
      signOff: {
        recommendations: '',
        commissioningStatus: '',
      },
    }) as Map<string, any>;
  };

  const [allResults, setAllResults] = useState(Map({}));
  const [submitAttempted, setSubmitAttempted] = useState(false);

  useEffect(() => {
    dispatch(fetchCheckSheetTasks('commissioning-comm-loop-v2-general'));
  }, [dispatch]);

  useEffect(() => {
    if (!reportId && generalTasks && generalTasks.size > 0) {
      setAllResults(buildEmptyResults(generalTasks));
    }
  }, [generalTasks, reportId]);

  useEffect(() => {
    if (open && !reportId) {
      setTabValue('1');
      setSubmitAttempted(false);
      if (generalTasks && generalTasks.size > 0) {
        setAllResults(buildEmptyResults(generalTasks));
      }
    }
  }, [open]);

  useEffect(() => {
    if (open && reportId) {
      dispatch(fetchCheckSheet(reportId, 'commissioning-comm-loop-v2-general'));
    }
  }, [open, reportId, dispatch]);

  useEffect(() => {
    if (reportId && report.get('checkSheet')) {
      setAllResults(Map(report));
    }
  }, [report, reportId]);

  if (!open) {
    return null;
  }

  if (reportId && !report.get('checkSheet')) {
    return null;
  }

  const handleTabChange = (_event: React.SyntheticEvent, newValue: string) => {
    setTabValue(newValue);
  };

  const statusError = submitAttempted && !allResults.getIn(['signOff', 'commissioningStatus']);

  const next = () => setTabValue((parseInt(tabValue) + 1).toString());
  const previous = () => setTabValue((parseInt(tabValue) - 1).toString());

  const submit = () => {
    if (!allResults.getIn(['signOff', 'commissioningStatus'])) {
      setSubmitAttempted(true);
      setTabValue(SIGN_OFF_TAB);
      return;
    }
    const values: any = allResults.toJS();
    const c = [...(values['commissioning-comm-loop-v2-general'] || [])];
    let payload = allResults
      .setIn(['info', 'deviceId'], deviceId || null)
      .set('checkSheet', fromJS(c))
      .set('signOff', allResults.get('signOff'));
    if (reportId) {
      payload = payload.set('id', reportId);
      dispatch(updateCheckSheet(payload, deviceId, 'commissioning-comm-loop-v2'));
    } else {
      dispatch(saveCheckSheet('commissioning-comm-loop-v2', payload, deviceId));
    }
  };

  const change = (path, value) => {
    setAllResults(prev => prev.setIn(path, value));
  };

  const titleTag = device ? device.get('tag') : allResults?.getIn(['extras', 'projectInfo', 'commLoopNumber']);

  const DEVICE_LIST_COLUMNS = [
    { key: 'controllerTag', label: t('commissioningCommLoop.deviceList.controllerTag') },
    { key: 'controllerType', label: t('commissioningCommLoop.deviceList.controllerType') },
    { key: 'modbusAddress', label: t('commissioningCommLoop.deviceList.modbusAddress') },
    { key: 'connectionType', label: t('commissioningCommLoop.deviceList.connectionType') },
    { key: 'serialConfig', label: t('commissioningCommLoop.deviceList.serialConfig') },
    { key: 'verified', label: t('commissioningCommLoop.deviceList.verified') },
  ];

  const DEFICIENCIES_COLUMNS = [
    { key: 'item', label: t('commissioningCommLoop.deficiencies.item') },
    { key: 'type', label: t('commissioningCommLoop.deficiencies.type') },
    { key: 'notes', label: t('commissioningCommLoop.deficiencies.notes') },
  ];

  return (
    <Modal open={props.open} onClose={props.handleClose} className='commissioning-comm-loop-modal'>
      <div className={`modal testing-report-modal commissioning-modal${isPhoneView ? ' modal-no-padding-sides' : ''}`}>
        {!isMobile && (
          <h2>{t('commissioningCommLoop.modalTitle')} {titleTag && <b>({titleTag})</b>}</h2>
        )}
        {isLoadingTasks ? (
          <div className='commissioning-comm-loop-loading-spinner'>
            <LogoSpinner size={100} />
          </div>
        ) : (
          <>
            <Box className='commissioning-comm-loop-tabs-box'>
              <Tabs
                value={tabValue}
                onChange={handleTabChange}
                textColor='primary'
                indicatorColor='primary'
                aria-label="commissioning-comm-loop-tabs"
                variant="scrollable"
                scrollButtons="auto"
              >
                <Tab value="1" label={<span className='tab-label-size'>{t('commissioningCommLoop.tabs.info')}</span>} />
                <Tab value="2" label={<span className='tab-label-size'>{t('commissioningCommLoop.tabs.cellularRadio')}</span>} />
                <Tab value="3" label={<span className='tab-label-size'>{t('commissioningCommLoop.tabs.commChecks')}</span>} />
                <Tab value="4" label={<span className='tab-label-size'>{t('commissioningCommLoop.tabs.deviceList')}</span>} />
                <Tab value="5" label={<span className='tab-label-size'>{t('commissioningCommLoop.tabs.deficiencies')}</span>} />
                <Tab value="6" label={<span className='tab-label-size'>{t('commissioningCommLoop.tabs.signOff')}</span>} />
              </Tabs>
            </Box>

            {tabValue === '1' && (
              <CommLoopInfo
                readOnly={readOnly}
                data={allResults?.get('extras')}
                onChange={change}
              />
            )}
            {tabValue === '2' && (
              <CommLoopInfo
                readOnly={readOnly}
                data={allResults?.get('extras')}
                onChange={change}
                sectionKeys={CELLULAR_RADIO_SECTION_KEYS}
              />
            )}
            {tabValue === '3' && (
              <CheckSheet
                readOnly={readOnly}
                onChange={change}
                id='commissioning-comm-loop-v2-general'
                title={t('commissioningCommLoop.tabs.commChecks')}
                data={allResults?.get('commissioning-comm-loop-v2-general')}
                checkSheetType="commissioning-comm-loop-v2-general"
              />
            )}
            {tabValue === '4' && (
              <CircuitDynamicTable
                title={t('commissioningCommLoop.tabs.deviceList')}
                columns={DEVICE_LIST_COLUMNS}
                data={allResults?.getIn(['extras', 'deviceList']) as List<any>}
                extrasKey="deviceList"
                onChange={change}
                readOnly={readOnly}
              />
            )}
            {tabValue === '5' && (
              <CircuitDynamicTable
                title={t('commissioningCommLoop.tabs.deficiencies')}
                columns={DEFICIENCIES_COLUMNS}
                data={allResults?.getIn(['extras', 'deficiencies']) as List<any>}
                extrasKey="deficiencies"
                onChange={change}
                readOnly={readOnly}
                typeLegend={t('commissioningCommLoop.deficiencies.legend')}
              />
            )}
            {tabValue === SIGN_OFF_TAB && (
              <CommissioningSignOff
                readOnly={readOnly}
                data={allResults?.get('signOff')}
                onChange={change}
                statusError={statusError}
              />
            )}

            <div className='button-bar stay-at-bottom'>
              <Button onClick={() => props.handleClose()}>{t('common.cancel')}</Button>
              {tabValue !== '1' && <Button onClick={previous}>{t('common.previous')}</Button>}
              {tabValue === SIGN_OFF_TAB
                ? <Button onClick={submit} disabled={readOnly} loading={isSaveLoading} cta>{reportId ? t('common.save') : t('common.submit')}</Button>
                : <Button className='next-button' onClick={next} cta>{t('common.next')}</Button>
              }
            </div>
          </>
        )}
      </div>
    </Modal>
  );
}

CommissioningCommLoopReport.propTypes = {
  handleClose: PropTypes.func.isRequired,
  open: PropTypes.bool.isRequired,
  deviceId: PropTypes.string,
  reportId: PropTypes.string,
};
